import { ReactNode, useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AirlineSelector } from './AirlineSelector';
import { useDevice } from '../contexts/DeviceContext';

interface DashboardLayoutProps {
  children: ReactNode;
  hideChrome?: boolean;  // Hide header, nav, footer for fullscreen views (assessment editors)
}

export default function DashboardLayout({ children, hideChrome = false }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Get user metadata
  const userRole = user?.user_metadata?.role || 'Unknown';
  const userName = user?.user_metadata?.full_name || user?.email || 'User';

  // Check if user is admin or instructor
  const isAdminOrInstructor = userRole === 'admin' || userRole === 'instructor';

  // Navigation items
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/files', label: 'Files', icon: '📁' },
    { path: '/sessions', label: 'Sessions', icon: '📝' },
  ];

  // Add admin tab for authorized users
  if (isAdminOrInstructor) {
    navItems.push({ path: '/admin', label: 'Admin', icon: '⚙️' });
  }

  const isActive = (path: string) => location.pathname === path;

  // Fullscreen mode: hide header, nav, footer for assessment editors
  if (hideChrome) {
    return (
      <div className="min-h-screen bg-white">
        <main className="h-screen overflow-auto">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Deep Navy (#0B2C45) with white logo */}
      <header className="header-bar shadow-lg sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-1.5">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/dashboard" className="group flex items-center">
              <img
                src="/images/wingmentor-logo.png"
                alt="WingMentor Logo"
                className="h-10 w-auto opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }}>
                <h1 className="text-xl font-heading font-bold text-white">WingMentor</h1>
                <p className="text-xs text-white/80 font-semibold">Competency Assessment</p>
              </div>
            </Link>

            {/* Spacer */}
            <div className="flex-1" />

            {/* User Menu Dropdown */}
            <UserMenu userName={userName} userRole={userRole} onSignOut={handleSignOut} />
          </div>
        </div>
      </header>

      {/* Navigation Tabs (desktop only) */}
      <nav className="hidden md:block bg-white border-b-2 border-steel/10 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center space-x-2 px-4 py-2.5 text-sm font-semibold border-b-3 transition-all
                    ${
                      isActive(item.path)
                        ? 'border-navy text-navy bg-navy/5'
                        : 'border-transparent text-steel hover:text-navy hover:bg-white hover:border-steel/20'
                    }
                  `}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Airline Selector - Right side of navigation */}
            <div className="py-2">
              <AirlineSelector />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isMobile ? 'pb-24' : ''}`}>
        {children}
      </main>

      {/* Footer - Steel Gray background (desktop only) */}
      <footer className="hidden md:block bg-steel border-t-2 border-signal/20 mt-6">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-sm text-white">
              © 2025 WingMentor - Competency Assessment Platform
            </p>
            <p className="text-xs text-white/80">
              Precision • Trust • Professionalism • Innovation
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-steel/20 pb-safe z-50"
          style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-center justify-around px-2 py-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center min-w-[48px] min-h-[48px] px-2 py-1 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'text-navy bg-navy/5'
                    : 'text-steel hover:text-navy'
                }`}
              >
                <span className="text-xl leading-none">{item.icon}</span>
                <span className="text-[10px] font-semibold mt-0.5">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

function UserMenu({
  userName,
  userRole,
  onSignOut,
}: {
  userName: string;
  userRole: string;
  onSignOut: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"        
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
      >
        <div className="text-right">
          <p className="text-sm font-medium text-white">{userName}</p>
          <p className="text-xs text-white/70 capitalize">{userRole}</p>
        </div>
        <ChevronDown
          size={16}
          className={`text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          <Link
            to="/settings"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Settings size={16} />
            Settings
          </Link>
          <button
            onClick={() => {
              setIsOpen(false);
              onSignOut();
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
